/**
 * HUD modals: character sheet, inventory, skill tree, quest log, class
 * transfer, the NPC dialog, the merchant and the system menu.
 *
 * Each builder returns a detached backdrop element; the HUD owns mounting and
 * teardown, and rebuilds the open one whenever the state changes. Rebuilding
 * beats diffing here — these are a few dozen nodes that only exist while the
 * player is standing still reading them.
 */

import { SKILLS, ITEMS, QUESTS, CLASS_PROFILES, TELEPORTS } from "../../game/worldData.js";
import { audio } from "../../game/audio.js";

export const MODAL_TITLES = {
    character: "Character Sheet [C]",
    inventory: "Inventory [I]",
    skills: "Skills [K]",
    quests: "Quest Log [U]",
    classes: "Class Transfer",
    system: "System Menu [P]",
    npc: "Dialog",
    shop: "Merchant",
    sound: "Toggle Audio",
};

const EQUIP_LAYOUT = [
    [null, "helmet", null],
    ["weapon", "chest", "shield"],
    ["gloves", "legs", "boots"],
    ["necklace", "ring", "earring"],
];

const SLOT_LABELS = {
    weapon: "Weapon", shield: "Shield", helmet: "Head", chest: "Chest",
    legs: "Legs", gloves: "Hands", boots: "Feet", necklace: "Neck",
    ring: "Ring", earring: "Ear",
};

/**
 * @param {string} name
 * @param {import("./hud.js").Hud} hud
 * @returns {HTMLElement|null}
 */
export function openModal(name, hud) {
    switch (name) {
        case "character": return characterModal(hud);
        case "inventory": return inventoryModal(hud);
        case "skills": return skillsModal(hud);
        case "quests": return questsModal(hud);
        case "classes": return classesModal(hud);
        case "system": return systemModal(hud);
        case "npc": return npcModal(hud);
        case "shop": return shopModal(hud);
        default: return null;
    }
}

// ------------------------------------------------------------- scaffold

function shell(hud, { title, sub = "", width = "", body, footer }) {
    const backdrop = document.createElement("div");
    backdrop.className = "backdrop";

    const modal = document.createElement("div");
    modal.className = `modal ${width}`.trim();

    const header = document.createElement("header");
    header.innerHTML =
        `<div><b>${title}</b><span class="sub">${sub}</span></div>` +
        `<button title="Close">✕</button>`;
    header.querySelector("button").addEventListener("click", () => {
        audio.playUiClick();
        hud.closeModal();
    });
    modal.appendChild(header);

    const bodyEl = document.createElement("div");
    bodyEl.className = "body";
    if (typeof body === "string") bodyEl.innerHTML = body;
    else if (body) bodyEl.appendChild(body);
    modal.appendChild(bodyEl);

    const footEl = document.createElement("footer");
    if (footer) footEl.appendChild(footer);
    const close = document.createElement("button");
    close.className = "btn ghost";
    close.textContent = "Close";
    close.addEventListener("click", () => {
        audio.playUiClick();
        hud.closeModal();
    });
    footEl.appendChild(close);
    modal.appendChild(footEl);

    backdrop.appendChild(modal);
    // Clicking the veil closes, clicking the panel does not.
    backdrop.addEventListener("mousedown", (e) => {
        if (e.target === backdrop) hud.closeModal();
    });
    return { backdrop, bodyEl, footEl };
}

function note(text) {
    const el = document.createElement("span");
    el.className = "note";
    el.textContent = text;
    return el;
}

// ------------------------------------------------------------ character

function characterModal(hud) {
    const s = hud.state.stats;
    const attr = (k, v, color) =>
        `<div class="tile center"><div class="k">${k}</div>
         <div class="v" style="color:${color}">${Math.round(v)}</div></div>`;
    const derived = (k, v, color) =>
        `<div class="tile"><span class="k">${k}</span>
         <span class="v" style="color:${color}">${v}</span></div>`;

    const html = `
      <div class="tile" style="margin-bottom:12px">
        <div><b style="font-size:15px">Hero</b>
          <div style="font-size:11px;color:#fcd34d">${hud.state.playerClass}</div></div>
        <div style="text-align:right">
          <div class="v">Lv ${s.level}</div>
          <div class="k mono">SP ${Math.floor(s.sp).toLocaleString()}</div></div>
      </div>
      <h4>Base attributes</h4>
      <div class="grid3">
        ${attr("STR", s.str, "#fcd34d")}${attr("DEX", s.dex, "#fcd34d")}${attr("CON", s.con, "#fcd34d")}
        ${attr("INT", s.int, "#7dd3fc")}${attr("WIT", s.wit, "#7dd3fc")}${attr("MEN", s.men, "#7dd3fc")}
      </div>
      <h4>Combat</h4>
      <div class="grid2">
        ${derived("P. Atk", Math.round(s.patk), "#fb7185")}
        ${derived("P. Def", Math.round(s.pdef), "#60a5fa")}
        ${derived("M. Atk", Math.round(s.matk), "#7dd3fc")}
        ${derived("M. Def", Math.round(s.mdef), "#c4b5fd")}
        ${derived("Crit rate", `${Math.round(s.critRate)}%`, "#fcd34d")}
        ${derived("Atk speed", Math.round(s.atkSpd), "#6ee7b7")}
        ${derived("Move speed", `${s.moveSpeed.toFixed(1)} m/s`, "#6ee7b7")}
        ${derived("Adena", Math.floor(s.adena).toLocaleString(), "#fbbf24")}
      </div>`;

    const { backdrop } = shell(hud, {
        title: "Character Status Sheet", width: "narrow", body: html,
    });
    return backdrop;
}

// ------------------------------------------------------------ inventory

function inventoryModal(hud) {
    const st = hud.state;
    const body = document.createElement("div");
    body.innerHTML = `<div class="grid2" style="align-items:start;gap:16px">
        <div><h4>Equipment</h4><div class="doll"></div></div>
        <div>
          <h4>Backpack <span style="float:right;color:#fbbf24">
            ${Math.floor(st.stats.adena).toLocaleString()} 💰</span></h4>
          <div class="bag"></div>
        </div>
      </div>`;

    const doll = body.querySelector(".doll");
    for (const row of EQUIP_LAYOUT) {
        for (const slot of row) {
            const cell = document.createElement("div");
            if (!slot) {
                cell.style.visibility = "hidden";
                cell.className = "cell";
                doll.appendChild(cell);
                continue;
            }
            const eq = st.equipment[slot];
            cell.className = eq ? "cell filled" : "cell empty";
            cell.title = eq ? `${eq.item.name} — click to unequip` : SLOT_LABELS[slot];
            cell.innerHTML = eq
                ? `${eq.item.icon}<span class="n">${eq.item.grade.replace("-Grade", "")}</span>`
                : SLOT_LABELS[slot];
            if (eq) {
                cell.addEventListener("click", () => {
                    audio.playUiClick();
                    st.unequipItem(slot);
                });
            }
            doll.appendChild(cell);
        }
    }

    const bag = body.querySelector(".bag");
    for (const stack of st.inventory) {
        const cell = document.createElement("div");
        cell.className = `cell ${stack.isEquipped ? "equipped" : "filled"}`;
        cell.title = `${stack.item.name} — ${stack.item.description}`;
        cell.innerHTML =
            `${stack.item.icon}` +
            (stack.quantity > 1 ? `<span class="n">${stack.quantity}</span>` : "") +
            (stack.isEquipped ? `<span class="e">E</span>` : "");
        cell.addEventListener("click", () => {
            audio.playUiClick();
            hud.game.useInventoryItem(stack);
        });
        bag.appendChild(cell);
    }
    if (!st.inventory.length) {
        bag.innerHTML = `<div class="note" style="grid-column:1/-1">Empty.</div>`;
    }

    const { backdrop } = shell(hud, {
        title: "Inventory & Equipment",
        width: "wide",
        body,
        footer: note("Click gear to equip or unequip. Click a potion to drink it."),
    });
    return backdrop;
}

// --------------------------------------------------------------- skills

function skillsModal(hud) {
    const st = hud.state;
    const body = document.createElement("div");

    for (const skill of Object.values(SKILLS)) {
        const level = st.skillLevel(skill.id);
        const card = document.createElement("div");
        card.className = "row-card";
        card.innerHTML = `
          <div class="ic">${skill.icon}</div>
          <div class="meta">
            <b>${skill.name}</b>
            <span class="chip amber" style="margin-left:6px">Lv ${level}</span>
            <p>${skill.description}</p>
            <div class="stats">
              <span class="mp">MP ${skill.mpCost}</span>
              <span class="cd">CD ${skill.cooldown}s</span>
              <span>Cast ${skill.castTime}s</span>
              <span>Range ${skill.range} m</span>
              <span>Req Lv ${skill.requiredLevel}</span>
            </div>
          </div>`;

        const btn = document.createElement("button");
        btn.className = "btn";
        btn.textContent = `Learn · ${skill.spCost} SP`;
        const affordable = st.stats.sp >= skill.spCost && st.stats.level >= skill.requiredLevel;
        btn.disabled = !affordable || skill.spCost === 0;
        btn.addEventListener("click", () => {
            audio.playUiClick();
            st.learnSkill(skill.id);
        });
        card.appendChild(btn);
        body.appendChild(card);
    }

    const { backdrop } = shell(hud, {
        title: "Skill Mastery Tree",
        sub: `Available SP: ${Math.floor(st.stats.sp).toLocaleString()}`,
        body,
    });
    return backdrop;
}

// --------------------------------------------------------------- quests

function questsModal(hud) {
    const st = hud.state;
    const body = document.createElement("div");
    const entries = Object.entries(st.quests);

    if (!entries.length) {
        body.innerHTML =
            `<div class="note">No quests accepted. Speak to Grand Master Gallint.</div>`;
    }

    for (const [id, progress] of entries) {
        const def = QUESTS[id];
        if (!def) continue;
        const pct = Math.min(100, (progress.currentKills / def.requiredKills) * 100);
        const reward = ITEMS[def.rewardItemId];
        const card = document.createElement("div");
        card.className = "row-card";
        card.style.display = "block";
        card.innerHTML = `
          <div style="display:flex;justify-content:space-between;align-items:center">
            <b>${def.title}</b>
            ${progress.isTurnedIn
                ? `<span class="ready" style="color:#94a3b8;border-color:#334155;background:#0f172a">Completed</span>`
                : progress.isCompleted
                    ? `<span class="ready">Ready to turn in</span>`
                    : `<span class="mono" style="color:#fbbf24">${progress.currentKills} / ${def.requiredKills}</span>`}
          </div>
          <p style="font-size:11px;color:#94a3b8;margin:6px 0">${def.description}</p>
          <div class="track quest-bar"><div class="fill xp" style="width:${pct}%"></div></div>
          <div class="stats" style="font-size:11px;color:#6ee7b7">
            🎁 ${def.rewardAdena.toLocaleString()} adena · ✨ ${def.rewardExp.toLocaleString()} EXP
            ${reward ? `· ${reward.icon} ${reward.name}` : ""}
          </div>`;

        if (progress.isCompleted && !progress.isTurnedIn) {
            const btn = document.createElement("button");
            btn.className = "btn";
            btn.style.marginTop = "8px";
            btn.textContent = "Turn in";
            btn.addEventListener("click", () => {
                audio.playUiClick();
                hud.game.turnInQuest(id);
            });
            card.appendChild(btn);
        }
        body.appendChild(card);
    }

    const { backdrop } = shell(hud, { title: "Quest Log", body });
    return backdrop;
}

// -------------------------------------------------------------- classes

function classesModal(hud) {
    const st = hud.state;
    const body = document.createElement("div");
    const grid = document.createElement("div");
    grid.className = "grid2";

    for (const profile of Object.values(CLASS_PROFILES)) {
        const card = document.createElement("button");
        const selected = st.playerClass === profile.name;
        card.className = `class-card${selected ? " on" : ""}`;
        card.innerHTML = `
          <span class="g">${profile.icon}</span>
          <b>${profile.name}${selected ? " · active" : ""}</b>
          <p>${profile.description}</p>
          <div class="w">Starting weapon: ${profile.startingWeapon.replace(/_/g, " ")}</div>`;
        card.addEventListener("click", () => {
            audio.playUiClick();
            st.setClass(profile.name);
            hud.game.onClassChanged?.();
        });
        grid.appendChild(card);
    }
    body.appendChild(grid);

    const { backdrop } = shell(hud, {
        title: "Class Transfer",
        width: "wide",
        body,
        footer: note("Changing class re-rolls your attributes and starting weapon."),
    });
    return backdrop;
}

// --------------------------------------------------------------- system

function systemModal(hud) {
    const body = document.createElement("div");
    body.innerHTML = `
      <h4>Controls</h4>
      <div class="keys">
        <span>Move / select</span><b>Left click</b>
        <span>Rotate camera</span><b>Right drag</b>
        <span>Zoom</span><b>Wheel</b>
        <span>Hotbar</span><b>F1 – F12</b>
        <span>Snow surf</span><b>Hold R</b>
        <span>Chat</span><b>Enter</b>
        <span>Inventory</span><b>I</b>
        <span>Character</span><b>C</b>
        <span>Skills</span><b>K</b>
        <span>Quests</span><b>U</b>
        <span>Close / deselect</span><b>Esc</b>
        <span>Render settings</span><b>\`</b>
      </div>
      <h4>Save data</h4>`;

    const reset = document.createElement("button");
    reset.className = "btn danger";
    reset.textContent = "Delete saved character and reload";
    reset.addEventListener("click", () => {
        if (!window.confirm("Delete the saved character? This cannot be undone.")) return;
        hud.state.resetSave();
        window.location.reload();
    });
    body.appendChild(reset);

    const { backdrop } = shell(hud, { title: "System Menu", width: "narrow", body });
    return backdrop;
}

// ----------------------------------------------------------- npc dialog

function npcModal(hud) {
    const npc = hud.game.activeNpc;
    if (!npc) return null;
    const st = hud.state;

    const body = document.createElement("div");
    body.style.padding = "0";

    const greet = document.createElement("div");
    greet.className = "greet";
    greet.innerHTML = `<b>"${npc.dialogHeader}"</b>${npc.dialogText}`;
    body.appendChild(greet);

    const list = document.createElement("div");
    list.style.padding = "14px 16px";
    body.appendChild(list);

    for (const opt of npc.options) {
        const btn = document.createElement("button");
        btn.className = "opt";
        let label = opt.label;

        if (opt.action === "quest") {
            const def = QUESTS[opt.targetId];
            const progress = st.quests[opt.targetId];
            if (progress?.isTurnedIn) {
                label = `${def.title} — completed`;
                btn.disabled = true;
            } else if (progress?.isCompleted) {
                label = `Turn in: ${def.title}`;
            } else if (progress) {
                label = `In progress: ${def.title} (${progress.currentKills}/${def.requiredKills})`;
                btn.disabled = true;
            }
        }

        btn.innerHTML = `${label} <span style="float:right">➔</span>`;
        btn.addEventListener("click", () => {
            audio.playUiClick();
            hud.game.npcAction(npc, opt);
        });
        list.appendChild(btn);
    }

    const { backdrop, bodyEl } = shell(hud, {
        title: npc.name, sub: npc.title, body,
    });
    bodyEl.style.padding = "0";
    return backdrop;
}

// ----------------------------------------------------------------- shop

function shopModal(hud) {
    const st = hud.state;
    const body = document.createElement("div");

    const buy = document.createElement("div");
    buy.innerHTML = `<h4>For sale</h4>`;
    for (const item of Object.values(ITEMS)) {
        const bundle = item.type === "consumable" ? 10 : 1;
        const cost = item.price * bundle;
        const row = document.createElement("div");
        row.className = "row-card";
        row.innerHTML = `
          <div class="ic">${item.icon}</div>
          <div class="meta">
            <b>${item.name}</b>${bundle > 1 ? ` <span class="k">× ${bundle}</span>` : ""}
            <p>${item.description}</p>
            <div class="stats"><span class="cd">${cost.toLocaleString()} adena</span>
              <span>${item.grade}</span></div>
          </div>`;
        const btn = document.createElement("button");
        btn.className = "btn";
        btn.textContent = "Buy";
        btn.disabled = st.stats.adena < cost;
        btn.addEventListener("click", () => {
            audio.playUiClick();
            hud.game.buyItem(item.id, bundle, cost);
        });
        row.appendChild(btn);
        buy.appendChild(row);
    }
    body.appendChild(buy);

    const { backdrop } = shell(hud, {
        title: "Trader Holly",
        sub: `${Math.floor(st.stats.adena).toLocaleString()} adena`,
        width: "wide",
        body,
        footer: note("Consumables are sold in bundles of ten."),
    });
    return backdrop;
}

export { TELEPORTS };
